import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { useShallow } from "zustand/react/shallow";
import { Button } from "components/common/Button";
import { Skeleton } from "components/common/Skeleton";
import { Spinner } from "components/common/Spinner";
import { Tooltip } from "components/common/Tooltip";
import {
  fetchOrgMembers,
  fetchOrgSpaces,
  isAbandoned,
  type Org,
  type OrgMember,
} from "@/lib/api/organizations";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  SectionCount,
  SettingsFrame,
  useSettingsSection,
  type SettingsSection,
} from "@/components/main/utils/settings/SettingsFrame";
import { GeneralPanel } from "./GeneralPanel";
import { GovernancePanel } from "./GovernancePanel";
import { MembersPanel } from "./MembersPanel";
import { OrgMark } from "./OrgMark";
import { PlanUsagePanel } from "./PlanUsagePanel";
import { SearchPriceBanner } from "./SearchPriceBanner";
import { ModelProvidersPanel } from "./ModelProvidersPanel";
import { SpacesPanel } from "./SpacesPanel";

type Section =
  "general" | "members" | "spaces" | "governance" | "plan" | "models";

const isGovernor = (org: Org) =>
  org.is_personal || org.role === "owner" || org.role === "org_admin";

/// Fixed list per org type and role (DESIGN.md: "doesn't apply to this kind
/// of item at all" ⇒ leave out) — a personal org has no roster, and a plain
/// member governs nothing, so they get General alone (identity + Leave).
const sectionsFor = (
  org: Org,
  memberCount: number,
  orphanCount: number,
): SettingsSection<Section>[] => {
  const governs = isGovernor(org);
  // A personal org has no roster and no admin distinct from its owner, so
  // neither the Members list nor the governance log would say anything its
  // owner does not already know.
  const governsNonPersonal = !org.is_personal && governs;
  return [
    { id: "general", label: "General" },
    ...(governsNonPersonal
      ? [
          {
            id: "members" as const,
            label: "Members",
            adornment: <SectionCount count={memberCount} />,
          },
        ]
      : []),
    // Spaces stays for a personal org: its owner is its only governor, so this
    // is the one surface that can reappoint a manager on a space stranded there
    // (D49) — the rail cannot show a space nobody holds a grant on.
    ...(governs
      ? [
          {
            id: "spaces" as const,
            label: "Spaces",
            adornment: orphanCount > 0 && (
              <Tooltip
                title={`${orphanCount} space(s) here have no manager. Nobody owns the models inside until someone is appointed.`}
              >
                <span className="flex items-center gap-1 text-warning">
                  <PersonOffOutlinedIcon sx={{ fontSize: 12 }} />
                  <span className="font-mono text-small tabular-nums">
                    {orphanCount}
                  </span>
                </span>
              </Tooltip>
            ),
          },
        ]
      : []),
    ...(governsNonPersonal
      ? [{ id: "governance" as const, label: "Governance" }]
      : []),
    ...(governs
      ? [
          { id: "plan" as const, label: "Plan & usage" },
          { id: "models" as const, label: "Model providers" },
        ]
      : []),
  ];
};

/// Org settings — ONE component for both org types; `is_personal` decides
/// which menu items exist, never a second page. Reached from the switcher's
/// gear. Plain members see General only — identity, and the door out (D43).
export const OrgSettingsPage: React.FC = () => {
  const { org_id } = useParams();
  const navigate = useNavigate();
  const { orgs, activeOrgId, switchOrg, setSelectedSpace } = useOrgContextStore(
    useShallow((s) => ({
      orgs: s.orgs,
      activeOrgId: s.activeOrgId,
      switchOrg: s.switchOrg,
      setSelectedSpace: s.setSelectedSpace,
    })),
  );

  // Nobody goes looking for an orphan, so the menu carries the count (D49).
  // Read for THIS org: the store holds the ACTIVE org's spaces, which is a
  // different org whenever these settings are opened from the org list.
  const [orphanCount, setOrphanCount] = useState(0);
  const reloadOrphanCount = useCallback(async () => {
    if (!org_id) return;
    try {
      const rows = await fetchOrgSpaces(org_id);
      setOrphanCount(rows.filter(isAbandoned).length);
    } catch {
      setOrphanCount(0);
    }
  }, [org_id]);
  useEffect(() => {
    void reloadOrphanCount();
  }, [reloadOrphanCount]);
  const org = orgs.find((o) => o.org_id === org_id);

  const [members, setMembers] = useState<OrgMember[]>([]);
  // The roster AND the org row: `org.role` is what the Danger buttons and the
  // sole-owner notice read, and a role change here can be the CALLER's own —
  // promoting a second owner has to stop the page still calling you the last.
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);
  const reloadMembers = useCallback(async () => {
    if (!org_id) return;
    const [roster] = await Promise.all([
      fetchOrgMembers(org_id),
      refreshOrgs(),
    ]);
    setMembers(roster);
  }, [org_id, refreshOrgs]);
  useEffect(() => {
    void reloadMembers();
  }, [reloadMembers]);

  // Until the org list lands, the frame shows with General alone.
  const sections = org
    ? sectionsFor(org, members.length, orphanCount)
    : [{ id: "general" as const, label: "General" }];
  // The menu differs per org and role, and switching org carries the hash over.
  const [section, setSection] = useSettingsSection<Section>(sections, !!org);

  if (!org && orgs.length > 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <span className="text-fg-regular">
          This organization does not exist here.
        </span>
        <Button variant="default" onClick={() => navigate("/overview")}>
          Back to overview
        </Button>
      </div>
    );
  }

  return (
    <SettingsFrame
      crumbs={[
        {
          icon: org && <OrgMark org={org} size="sm" />,
          label: org ? org.name : <Skeleton width="6rem" />,
          // To this org's Overview, which may not be the active org's.
          onClick: async () => {
            if (org && activeOrgId !== org.org_id) await switchOrg(org.org_id);
            setSelectedSpace(null);
            navigate("/overview");
          },
        },
      ]}
      closeLabel="Close organization settings"
      onClose={() => navigate("/overview")}
      navLabel="Organization settings sections"
      sections={sections}
      section={section}
      onSection={setSection}
    >
      {!org && (
        <span
          className="flex items-center item-height"
          aria-label="Loading organization"
        >
          <Spinner />
        </span>
      )}
      {org && <SearchPriceBanner orgId={org.org_id} />}
      {org && section === "general" && (
        <GeneralPanel key={org.org_id} org={org} members={members} />
      )}
      {org && section === "members" && (
        <MembersPanel org={org} members={members} onChanged={reloadMembers} />
      )}
      {org && section === "spaces" && (
        <SpacesPanel org={org} members={members} />
      )}
      {org && section === "governance" && <GovernancePanel org={org} />}
      {org && section === "plan" && (
        <PlanUsagePanel org={org} memberCount={members.length} />
      )}
      {org && section === "models" && <ModelProvidersPanel org={org} />}
    </SettingsFrame>
  );
};
